import "./relationshipView.scss";

import { List, Map } from "immutable";
import _ from "lodash";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import { findDOMNode } from "react-dom";
import ImmutablePropTypes from "react-immutable-proptypes";

import { RELATIONSHIP_ENTITY_COLUMN_WIDTH, RELATIONSHIP_GROUP_LABEL_WIDTH } from "../../constants";
import { DragDropTypes } from "../../constants/dragDropTypes";
import { RELATIONSHIP_VIEW_HEADER_GROUP } from "../../constants/identifiers";
import { ADD_COLUMN_PREFIX } from "../../constants/idPrefixes";
import { scalePixelsToFontSize } from "../../constants/styles/dimensions";
import { PropTypeShapes } from "../../types";
import { moveItemInList } from "../../utils/listUtils";
import { getXYPositionInDOMElement } from "../../utils/mouseUtils";
import { asDropTarget } from "../dragAndDrop/DroppableComponent";
import Icon from "../interface/Icon";
import RelationshipColumnHeaderAddColumn from "./RelationshipColumnHeaderAddColumn";
import RelationshipColumnHeaderColumnView from "./RelationshipColumnHeaderColumnView";

const dropCallbacks = {
  onEnter: (_props, _dragItem, _dropTarget) => {},

  onLeave: (props, dragItem, dropTarget) => {
    return dropTarget.component.onLeave(props, dragItem, dropTarget);
  },

  getDragMultipleItemIds(props, dragItem, _dropTarget) {
    const returnResult = props.selectedItemIds.contains(dragItem.itemId)
      ? props.columns
        .filter(obj => props.selectedItemIds.contains(obj.id))
        .map(obj => obj.id)
        .toArray()
      : [dragItem.itemId];
    return returnResult;
  },

  getDragStartParams(_props, _dragItem, _positions, _dropTarget) {
    return {};
  },

  onHover: (props, dragItem, positions, dropTarget) => {
    return dropTarget.component.onHover(props, dragItem, positions, dropTarget);
  },

  onDrop: (props, dragItem, positions, dropTarget) => {
    return dropTarget.component.onDrop(props, dragItem, positions, dropTarget);
  },

  getDropPreviewForItem: (_props, _dragItem, _dropTarget) => {
    // return dropTarget.component.getDropPreviewForItem(props, dragItem, dropTarget);
    return null;
  }
};

const isAddColumn = id => id.indexOf(ADD_COLUMN_PREFIX) > -1;

const renderColumns = (
  cols,
  isActiveView,
  selectedItemIds,
  onItemClick,
  viewId,
  allowColumnDrag,
  onLabelChange,
  onItemDoubleClick,
  showEntityColor,
  onCreateItemClick,
  label,
  labelDisplay
) =>
  cols
    .map((c, cIndex) => {
      // Note: When having nested arrays of elements,
      // for example:
      //
      // <group id="1"><column /><column />...</group>
      // <group id="2"><column /><column />...</group>
      // ...
      //
      // Make sure the keys of children are unique between the parents.
      // Othewise, the dom elements of the children will be re-created,
      // and the ref will be lost in the React component

      if (isAddColumn(c.id)) {
        return (
          <RelationshipColumnHeaderAddColumn
            key={`addColumn-${c.type}`}
            viewId={viewId}
            isActiveView={isActiveView}
            columnDisplayItem={c}
            onCreateItemClick={onCreateItemClick}
          />
        );
      }

      return (
        <RelationshipColumnHeaderColumnView
          key={c.id}
          viewId={viewId}
          index={cIndex}
          isActiveView={isActiveView}
          selectedItemIds={selectedItemIds}
          columnDisplayItem={c}
          onItemClick={() => {
            onItemClick(c.id);
          }}
          onItemDoubleClick={onItemDoubleClick}
          onLabelChange={onLabelChange}
          canDrag={allowColumnDrag}
          columnColor={showEntityColor ? c.color : null}
          label={label}
          labelDisplay={labelDisplay}
        />
      );
    })
    .toArray();

class RelationshipHeaderGroupView extends PureComponent {
  constructor(props) {
    super(props);
    this._dropIndex = null;
    this._itemDimensions = Map();
    this.state = { columns: List() };
  }

  componentWillMount() {
    this.copyLocalState(this.props);
  }

  componentDidMount() {
    this._node = findDOMNode(this);
    this.props.registerDropTarget(this, dropCallbacks);
  }

  componentWillReceiveProps(nextProps) {
    this.copyLocalState(nextProps);
  }

  copyLocalState(props) {
    this.setState({
      columns: props.columns
    });
  }

  onHover(_props, dragItem, positions, _dropTarget) {
    // If the dragged item does not belong in this group, then block it
    if (dragItem.itemType === DragDropTypes.TABLE_COLUMN) {
      return {
        message: "Cannot be dropped in relationship view",
        messageType: "error"
      };
    }

    const columnExists = this.props.columns.find(c => c.id === dragItem.itemId);
    if (!columnExists || dragItem.sourceView !== this.props.viewId) {
      return {
        message: "Only accepts items dragged from within its own header group",
        messageType: "error"
      };
    }

    const dragLeft = (positions.sourceRect.left + positions.sourceRect.right) / 2;

    const positionInNode = getXYPositionInDOMElement(dragLeft, 0, this._node);
    if (!positionInNode) {
      return {
        message: "Unexpected error during dragging",
        messageType: "error"
      };
    }

    const groupHeaderWidth = this.props.showLabel
      ? scalePixelsToFontSize(RELATIONSHIP_GROUP_LABEL_WIDTH, this.props.fontSize)
      : 0;
    const dropX = positionInNode.x - groupHeaderWidth;
    const columnWidth = scalePixelsToFontSize(RELATIONSHIP_ENTITY_COLUMN_WIDTH, this.props.fontSize);

    const dropIndex = Math.min(Math.max(0, Math.floor(dropX / columnWidth)), this.props.columns.size - 2);

    // If this is a new dropIndex value, then we need to update our state
    if (dropIndex !== this._dropIndex) {
      const oldIndex = this.props.columns.findIndex(c => c.id === dragItem.itemId);
      const oldLeft = this.props.columns.get(0).left;
      let columns = moveItemInList(this.props.columns, oldIndex, dropIndex);

      let globalLeft = oldLeft;
      columns = columns.map(c => {
        globalLeft += c.width;
        return c.set("left", globalLeft - c.width);
      });

      this._dropIndex = dropIndex;
      this.setState({
        columns
      });
    }
  }

  onLeave(_props, _dragItem, _dropTarget) {
    this._dropIndex = null;
    this.setState({
      columns: this.props.columns
    });
  }

  onDrop(props, dragItem, _positions, _dropTarget) {
    if (_.isNil(this._dropIndex)) {
      this.setState({
        columns: this.props.columns
      });
      return;
    }

    // Work out the target for this drop
    let targetId = null;
    let placeAfter = false;
    if (this._dropIndex < this.state.columns.size) {
      targetId = this.props.columns.get(this._dropIndex).id;
      placeAfter = false;
    } else {
      targetId = this.props.columns.last().id;
      placeAfter = true;
    }
    // Now move the dragged item to the correct index...
    this.props.onMoveItem(dragItem.itemType, dragItem.multipleItemIds, targetId, placeAfter);
    this._dropIndex = null;
  }

  getGroupStyle() {
    const { fontSize, width, showLabel } = this.props;
    const SCALED_GROUP_LABEL_WIDTH = scalePixelsToFontSize(RELATIONSHIP_GROUP_LABEL_WIDTH, fontSize);
    const calWidth = showLabel ? width : width - SCALED_GROUP_LABEL_WIDTH;
    return {
      width: calWidth + "px",
      flex: `0 0 ${calWidth}px`
    };
  }

  render() {
    const {
      id,
      showLabel,
      showArrow,
      label,
      onItemClick,
      viewId,
      onLabelClick,
      allowColumnDrag,
      onItemDoubleClick
    } = this.props;
    const columns = renderColumns(
      this.state.columns,
      this.props.isActiveView,
      this.props.selectedItemIds,
      onItemClick,
      viewId,
      allowColumnDrag,
      this.props.onLabelChange,
      onItemDoubleClick,
      this.props.showEntityColor,
      this.props.onCreateItemClick,
      this.props.label,
      this.props.labelDisplay
    );

    return (
      <div key={id} className="relationshipHeaderGroup" style={this.getGroupStyle()}>
        {showLabel ? (
          <div className="relationshipHeaderGroupLabelWrapper">
            <div className="relationshipHeaderGroupLabel" onClick={onLabelClick}>
              <span>{label}</span>
              {showArrow ? <Icon icon="downArrow" color="iconMono" /> : null}
            </div>
          </div>
        ) : null}
        {columns}
      </div>
    );
  }
}

RelationshipHeaderGroupView.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  labelDisplay: PropTypes.string,
  fontSize: PropTypes.number,
  width: PropTypes.number.isRequired,
  showLabel: PropTypes.bool.isRequired,
  showArrow: PropTypes.bool.isRequired,
  columns: PropTypeShapes.relationshipColumnList,
  onItemClick: PropTypes.func.isRequired,
  onItemDoubleClick: PropTypes.func.isRequired,
  onLabelClick: PropTypes.func,
  onLabelChange: PropTypes.func,
  viewId: PropTypes.string.isRequired,
  isActiveView: PropTypes.bool.isRequired,
  selectedItemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  onMoveItem: PropTypes.func.isRequired,
  showEntityColor: PropTypes.bool.isRequired,
  onCreateItemClick: PropTypes.func.isRequired,
  allowColumnDrag: PropTypes.bool,

  // From asDropTarget
  registerDropTarget: PropTypes.func.isRequired
};

RelationshipHeaderGroupView.defaultProps = {
  showArrow: false
};

export default asDropTarget(RelationshipHeaderGroupView, RELATIONSHIP_VIEW_HEADER_GROUP);
